/**
 * Krok 5: Galeria hodowli (zdjęcie główne + galeria)
 */
import { useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Camera, ImagePlus, Loader2, X, GripVertical, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { chunkedUpload, validateFile } from '@/utils/chunkedUpload';
import { getImageUrl } from '@/utils/imageUrl';
import { ImageSeoBadge } from '@/components/ImageSeoBadge';

const MAX_GALLERY = 12;

export default function Step5Gallery({ data, updateData }) {
  const coverRef = useRef(null);
  const logoRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const uploadCover = async (file) => {
    if (!file) return;
    const v = validateFile(file);
    if (!v.valid) return toast.error(v.error);
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await chunkedUpload(file, token);
      updateData({ cover_image: r.urls?.large || r.url });
      toast.success('Zdjęcie główne dodane');
    } catch { toast.error('Błąd uploadu'); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const v = validateFile(file);
    if (!v.valid) return toast.error(v.error);
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await chunkedUpload(file, token);
      updateData({ logo: r.urls?.medium || r.url });
      toast.success('Logo dodane');
    } catch { toast.error('Błąd uploadu logo'); }
    finally { setBusy(false); }
  };

  const uploadGallery = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const token = localStorage.getItem('token');
    const existing = data.gallery || [];
    const newUrls = [];
    for (const file of files) {
      if (existing.length + newUrls.length >= MAX_GALLERY) break;
      const v = validateFile(file);
      if (!v.valid) continue;
      try {
        const r = await chunkedUpload(file, token);
        newUrls.push(r.urls?.medium || r.url);
      } catch {}
    }
    updateData({ gallery: [...existing, ...newUrls].slice(0, MAX_GALLERY) });
    setBusy(false);
    if (newUrls.length) toast.success(`Dodano ${newUrls.length} zdjęć`);
  };

  const removeGalleryImg = (idx) => {
    updateData({ gallery: (data.gallery || []).filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-7" data-testid="wizard-v2-step-gallery">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-orange-500" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Galeria hodowli</h2>
        </div>
        <p className="text-gray-500 text-sm">
          Dobre zdjęcia to najsilniejszy magnes na klientów. Dodaj główne zdjęcie + galerię z codziennego życia.
        </p>
      </header>

      <ImageSeoBadge />

      {/* 0. Logo (opcjonalnie) */}
      <section className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">Logo hodowli <span className="text-gray-400 font-normal">(opcjonalnie)</span></Label>
        <p className="text-xs text-gray-500 -mt-1">Kwadratowe logo wyświetlane obok nazwy hodowli na profilu publicznym i w Hero Creator. PNG z przezroczystym tłem najlepiej.</p>
        <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
        <div className="flex items-center gap-4">
          {data.logo ? (
            <div className="relative w-24 h-24 rounded-2xl overflow-hidden border-2 border-orange-200 bg-gray-50 flex-shrink-0">
              <img src={getImageUrl(data.logo) || data.logo} alt="Logo" className="w-full h-full object-contain p-1" />
              <button type="button" onClick={() => updateData({ logo: null })} className="absolute top-1 right-1 w-6 h-6 bg-white/95 shadow rounded-full flex items-center justify-center" data-testid="wizard-v2-logo-remove">
                <X className="w-3 h-3 text-red-600" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => logoRef.current?.click()}
              disabled={busy}
              data-testid="wizard-v2-logo-upload"
              className="w-24 h-24 rounded-2xl border-2 border-dashed border-orange-300 bg-orange-50/40 hover:bg-orange-50 hover:border-orange-400 flex flex-col items-center justify-center gap-1 flex-shrink-0"
            >
              {busy ? <Loader2 className="w-6 h-6 text-orange-500 animate-spin" /> : (
                <>
                  <ImagePlus className="w-6 h-6 text-orange-500" />
                  <span className="text-[10px] font-semibold text-orange-600">Dodaj logo</span>
                </>
              )}
            </button>
          )}
          {data.logo && (
            <button
              type="button"
              onClick={() => logoRef.current?.click()}
              data-testid="wizard-v2-logo-change"
              className="px-3 py-2 rounded-lg bg-orange-50 hover:bg-orange-100 text-orange-600 text-sm font-medium flex items-center gap-1.5"
            >
              <Camera className="w-4 h-4" /> Zmień logo
            </button>
          )}
        </div>
      </section>

      {/* 1. Cover */}
      <section className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">1. Zdjęcie główne (hero)</Label>        <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} />
        {data.cover_image ? (
          <div className="relative rounded-2xl overflow-hidden border-2 border-orange-200 aspect-[16/9] bg-gray-100">
            <img src={getImageUrl(data.cover_image) || data.cover_image} alt="Hero" className="w-full h-full object-cover" />
            <button type="button" onClick={() => coverRef.current?.click()} className="absolute bottom-4 left-4 bg-white shadow-md px-3 py-2 rounded-lg text-sm font-medium text-gray-800 flex items-center gap-1.5" data-testid="wizard-v2-cover-change">
              <Camera className="w-4 h-4" /> Zmień zdjęcie
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => coverRef.current?.click()}
            disabled={busy}
            data-testid="wizard-v2-cover-upload"
            className="w-full aspect-[16/9] rounded-2xl border-2 border-dashed border-orange-300 bg-orange-50/40 hover:bg-orange-50 hover:border-orange-400 flex flex-col items-center justify-center gap-3"
          >
            {busy ? <Loader2 className="w-10 h-10 text-orange-500 animate-spin" /> : (
              <>
                <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center">
                  <ImagePlus className="w-8 h-8 text-orange-500" />
                </div>
                <p className="text-base font-semibold text-orange-600">Dodaj zdjęcie główne</p>
                <p className="text-xs text-gray-500">JPG / PNG / WEBP · do 10MB</p>
              </>
            )}
          </button>
        )}
      </section>

      {/* 2. Gallery */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-gray-900">2. Galeria hodowli</Label>
          <span className="text-xs text-gray-500">{(data.gallery || []).length}/{MAX_GALLERY}</span>
        </div>
        <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadGallery(Array.from(e.target.files || []))} />
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {(data.gallery || []).map((img, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 group" data-testid={`wizard-v2-gallery-item-${i}`}>
              <img src={getImageUrl(img) || img} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeGalleryImg(i)} className="absolute top-1 right-1 w-7 h-7 bg-white/95 shadow rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100">
                <X className="w-3.5 h-3.5 text-red-600" />
              </button>
            </div>
          ))}
          {(data.gallery || []).length < MAX_GALLERY && (
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              disabled={busy}
              data-testid="wizard-v2-gallery-add"
              className="aspect-square rounded-lg border-2 border-dashed border-orange-300 bg-orange-50/40 hover:bg-orange-50 flex flex-col items-center justify-center text-orange-500"
            >
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <><ImagePlus className="w-6 h-6" /><span className="text-[10px] font-medium mt-1">Dodaj zdjęcia</span></>}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
